import React from "react"
import { Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import {
  BankElement,
  BusinessElement,
  IndividualElement,
  PasswordElement,
  TaxElement,
  EntityElement,
  BrandElement,
  TerminalElement
} from "@tap-payments/auth-jsconnect"
import ApplePayDemo from "./applePay"
import ApplePayV18Demo from "./ApplePayV18/ApplePayV18"
import ZidPayDemo from "./ZidPayDemo"
import Connect from "./Connect"
import ConnectExpress from "./ConnectExpress"
import Board from "./Board"
import { allCountryISO2Values, getDeploymentRegion, sleep } from "./utils"
import { BUSINESS_COUNTRIES } from "./constants"
import { SettingAsyncData } from "./types"
import Auth from "./Auth"
import KYC from "./KYC"
import ApiPerformance from "./api-performance"

function App() {
  const [maturity, setMaturity] = React.useState<"full" | "express">("full")
  const [searchParams] = useSearchParams()
  const configToken = searchParams.get("config_token") as string
  const countryCode = searchParams.get("country_code")
  const [settingData, setSettingData] = React.useState<SettingAsyncData | undefined>(undefined)

  const location = useLocation()
  const navigate = useNavigate()
  const path = location.pathname.toLocaleLowerCase()
  const region = getDeploymentRegion()

  // TODO: Try to get business countries from Backend instead of static values
  const allowedCountries = React.useMemo(() => allCountryISO2Values(BUSINESS_COUNTRIES), [])
  const countryCodeRegex = /^\/[a-zA-Z]{2}(\/|$)/

  // allowed application routes that should not be treated as country codes
  const allowedPaths = [
    "applepay", "applepay-v18", "express", "tax", "bank", "business",
    "individual", "password", "entity", "brand", "terminal", "board",
    "kyc", "auth", "demo", "api-performance"
  ]

  const getCountryCode = () => {
    const p = path.replaceAll("/", "")
    return allowedCountries.includes(p) ? p : ""
  }

  React.useEffect(() => {
    if (configToken) return

    const code = getCountryCode()

    // If the path is a single segment that is NOT a valid country and NOT a known route,
    // redirect to root so the default flow starts clean
    const segment = path.replace(/^\//, "").replace(/\/$/, "")
    if (segment && !allowedCountries.includes(segment) && !allowedPaths.includes(segment)) {
      navigate(`/${location.search}${location.hash}`, { replace: true })
      return
    }

    if (!code) return

    // path has country code and not allowed
    if (countryCodeRegex.test(path) && !allowedCountries.includes(path.replaceAll("/", ""))) {
      const newUrl = location.pathname.replace(countryCodeRegex, `/${code}/`)
      navigate(`${newUrl}${location.search}${location.hash}`, { replace: true })
      return
    }
    // no country code no other paths
    else if (path === "/" || path === "") {
      const newUrl = location.pathname.replace(/^\//, `/${code}/`)
      navigate(`${newUrl}${location.search}${location.hash}`, { replace: true })
    }
  }, [countryCodeRegex, configToken, allowedCountries, allowedPaths, path, location, navigate])

  return (
    <Routes>
      <Route caseSensitive path='/applepay' element={<ApplePayDemo />} />
      <Route caseSensitive path='/applepay-v18' element={<ApplePayV18Demo />} />
      {configToken && maturity === "express" && (
        <Route caseSensitive path='/' element={<ConnectExpress configToken={configToken} settingData={settingData} />} />
      )}
      {configToken && maturity === "express" && (
        <Route caseSensitive path='/:country' element={<ConnectExpress configToken={configToken} settingData={settingData} />} />
      )}
      <Route
        caseSensitive
        path='/'
        element={
          <Connect
            countryCode={getCountryCode()}
            configToken={configToken}
            onMaturityChanged={async (maturity) => {
              setMaturity(maturity)
              if (maturity) await sleep(500)
            }}
            onSettingFetched={(data) => {
              if (configToken) setSettingData(data)
            }}
          />
        }
      />
      <Route
        caseSensitive
        path='/:country'
        element={
          <Connect
            countryCode={getCountryCode()}
            configToken={configToken}
            onMaturityChanged={async (maturity) => {
              setMaturity(maturity)
              if (maturity) await sleep(500)
            }}
            onSettingFetched={(data) => {
              if (configToken) setSettingData(data)
            }}
          />
        }
      />
      <Route caseSensitive path='/express' element={<ConnectExpress configToken={configToken} />} />
      <Route
        caseSensitive
        path='/tax'
        element={
          <TaxElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/bank'
        element={
          <BankElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/business'
        element={
          <BusinessElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/individual'
        element={
          <IndividualElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/password'
        element={
          <PasswordElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/entity'
        element={
          <EntityElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/brand'
        element={
          <BrandElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route
        caseSensitive
        path='/terminal'
        element={
          <TerminalElement
            open={true}
            scope='merchant'
            merchantDomain={window.location.origin}
            publicKey={"pk_test_lat64TEDSvHrUOYAxVRe28PQ"}
            appInfo={{
              name: "Tap Connect Demo"
            }}
            businessCountryCode={countryCode ?? "SA"}
            language={"en" as any}
            region={region}
            onError={(error: any) => {
              console.log(JSON.stringify({ error }))
            }}
            onFlowCompleted={(res: any) => console.log("onFlowCompleted", res)}
          />
        }
      />
      <Route caseSensitive path='/board' element={<Board />} />
      <Route caseSensitive path='/kyc' element={<KYC />} />
      <Route caseSensitive path='/auth' element={<Auth />} />
      <Route caseSensitive path='/demo' element={<ZidPayDemo />} />
      <Route caseSensitive path='/api-performance' element={<ApiPerformance />} />
    </Routes>
  )
}

export default App
